import { useState } from 'react';
import { PLANNER_DAY_LONG } from '../../utils/mealPlanHelpers.js';
import NightOffPicker from './NightOffPicker.jsx';
import './planner.css';

/**
 * Empty plan-day slot — day chip + “Add a dinner” CTA (Option 1 list header comps).
 * Past open nights stay quiet: day chip only, no “No dinner” / Past badge. Skip and
 * night-off chips are only passed in when the person already had the kitchen that
 * day — first-week onboarding nights stay quiet.
 * onAdd opens the recipe picker sheet for that day.
 * Mobile density reuses the assigned MobileMealCard day control sizing.
 */
export default function EmptyDayRow({
  dayKey,
  dateNum,
  isToday = false,
  density = 'desktop',
  onAdd = null,
  /** Past calendar day — locks add; shows quiet empty / skip affordances instead. */
  disabled = false,
  status = 'planned',
  onMarkSkipped = null,
  onNightOff = null,
}) {
  const longLabel = PLANNER_DAY_LONG[dayKey] || dayKey;
  const compact = density === 'mobile';
  const isPast = Boolean(disabled);
  const isSkipped = status === 'skipped';
  const canAdd = Boolean(onAdd) && !isPast;
  const canSkip = isPast && !isSkipped && Boolean(onMarkSkipped);
  const canNightOff = isPast && !isSkipped && Boolean(onNightOff);
  const quietPast = isPast && !isSkipped && !canSkip && !canNightOff;
  const chipAria = `${longLabel}${dateNum != null ? ` ${dateNum}` : ''}${isPast ? ', past' : ''}`;
  const [nightOffType, setNightOffType] = useState(null);

  return (
    <div
      className={[
        'meal-row',
        'meal-row--empty',
        compact ? 'meal-row--empty-mobile' : '',
        isToday ? 'meal-row--today' : '',
        isPast ? 'meal-row--empty-past' : '',
        quietPast ? 'meal-row--empty-past-quiet' : '',
        isSkipped ? 'meal-row--empty-skipped' : '',
      ].filter(Boolean).join(' ')}
    >
      {compact ? (
        <div
          className="mobile-meal-day-btn meal-row-day-chip--empty"
          aria-label={chipAria}
        >
          <div className="mobile-meal-day-bar" aria-hidden="true" />
          <div className="mobile-meal-day-stack">
            <div className="mobile-meal-day-letter" aria-hidden="true">
              {longLabel}
            </div>
            <div className="mobile-meal-day-date">{dateNum}</div>
          </div>
          {isToday && <div className="meal-row-today-dot" aria-hidden="true" />}
        </div>
      ) : (
        <div
          className="meal-row-day-chip meal-row-day-chip--empty"
          aria-label={chipAria}
        >
          <div className="meal-row-day-chip-bar" aria-hidden="true" />
          <div className="meal-row-day-label">{longLabel}</div>
          <div className="meal-row-day-date">{dateNum}</div>
          {isToday && <div className="meal-row-today-dot" aria-hidden="true" />}
        </div>
      )}

      {isPast ? (
        quietPast ? (
          <span className="sr-only">No dinner planned</span>
        ) : (
          <div className="meal-row-empty-past-body">
            {(isSkipped || canSkip) ? (
              <div className="meal-row-empty-past-top">
                {isSkipped ? (
                  <span className="meal-row-slot-status skipped">Skipped</span>
                ) : null}
                {canSkip ? (
                  <button
                    type="button"
                    className="planner-pick-remove-link meal-row-empty-skip"
                    onClick={onMarkSkipped}
                  >
                    Skip
                  </button>
                ) : null}
              </div>
            ) : null}
            {canNightOff ? (
              <div className="meal-row-resolve-night-off">
                <span className="meal-row-resolve-lead">Or it was a night off</span>
                <NightOffPicker
                  selected={nightOffType}
                  onSelect={setNightOffType}
                  commitOnSelect
                  onSave={async (typeId) => {
                    const ok = await onNightOff(typeId)
                    if (ok === false) setNightOffType(null)
                  }}
                />
              </div>
            ) : null}
          </div>
        )
      ) : (
        <button
          type="button"
          className="meal-row-empty-add"
          onClick={canAdd ? onAdd : undefined}
          disabled={!canAdd}
          aria-label={`Add a dinner for ${longLabel}`}
        >
          <span className="meal-row-add-plus" aria-hidden="true">+</span>
          Add a dinner
        </button>
      )}
    </div>
  );
}
